import React, { useState, useEffect, useCallback } from 'react';
import { Play, Music, RefreshCw, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { searchWorshipStreams } from '../services/youtubeService';
import { toast } from 'sonner';

// Utility function to truncate text
const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const YouTubeStreams = () => {
  const { t } = useTranslation();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(12);

  const fetchStreams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const liveStreams = await searchWorshipStreams(20);
      if (liveStreams.length === 0) {
        throw new Error(t('youtubeStreams.noStreams'));
      }
      setStreams(liveStreams);
      localStorage.setItem('worshipStreams', JSON.stringify(liveStreams));
      localStorage.setItem('lastFetchTime', Date.now().toString());
      toast.success(t('youtubeStreams.loaded', { count: liveStreams.length }), {
        position: 'bottom-right',
        duration: 2000,
      });
    } catch (err) {
      console.error('Error fetching YouTube streams:', err);
      const errorMessage = err.message.includes('quotaExceeded')
        ? t('youtubeStreams.quotaExceeded')
        : err.message || t('youtubeStreams.error');
      setError(errorMessage);
      toast.error(errorMessage, {
        position: 'bottom-right',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleRetry = useCallback(() => {
    setError(null);
    setDisplayedCount(12);
    fetchStreams();
  }, [fetchStreams]);

  const handleShowMore = useCallback(() => {
    setDisplayedCount((prev) => Math.min(prev + 4, streams.length));
  }, [streams.length]);

  useEffect(() => {
    localStorage.removeItem('worshipStreams');
    localStorage.removeItem('lastFetchTime');
    fetchStreams();
  }, [fetchStreams]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
          {[...Array(12)].map((_, i) => (
            <div key={`skeleton-${i}`} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
              <div className="aspect-video bg-gray-200" />
              <div className="p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-full mb-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-red-600">
        <p className="text-base sm:text-lg">{error}</p>
        <button
          onClick={handleRetry}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('youtubeStreams.retry')}
        </button>
      </div>
    );
  }

  if (streams.length === 0 && !loading && !error) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-gray-800">
          {t('youtubeStreams.live24_7')}
        </h2>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md max-w-2xl mx-auto">
          <div className="flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
            <p className="text-yellow-700 text-sm sm:text-base">{t('youtubeStreams.noStreams')}</p>
          </div>
          <button
            onClick={handleRetry}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('youtubeStreams.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden h-full mx-auto px-4 py-4 md:px-6 md:py-6">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-center text-gray-800">
        {t('youtubeStreams.live24_7')}
      </h2>
      <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto text-sm sm:text-base lg:text-lg">
        {t('youtubeStreams.description')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {streams.slice(0, displayedCount).map((stream) => (
          <motion.div
            key={stream.id.videoId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            <div className="relative">
              <img
                src={stream.snippet.thumbnails.medium?.url || ''}
                alt={stream.snippet.title}
                className="w-full aspect-video object-cover"
                loading="lazy"
              />
              <div
                className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => window.open(`https://www.youtube.com/watch?v=${stream.id.videoId}`, '_blank')}
              >
                <div className="bg-red-600 text-white rounded-full p-3 sm:p-4">
                  <Play className="w-5 h-5 sm:w-6 h-6" />
                </div>
              </div>
              <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1">
                <span className="animate-pulse h-1.5 w-1.5 bg-white rounded-full" />
                {t('youtubeStreams.live')}
              </div>
            </div>
            <div className="p-4">
              <h3
                className="font-semibold text-gray-800 line-clamp-2 text-sm sm:text-base h-12 sm:h-14 min-h-12"
                title={stream.snippet.title} // Add tooltip with full title
              >
                {truncateText(stream.snippet.title, 60)} {/* Truncate to 60 characters */}
              </h3>
              <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-2">
                <Music className="w-3 h-3 sm:w-4 h-4 mr-1" />
                <span className="truncate">{stream.snippet.channelTitle}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {displayedCount < streams.length && (
        <div className="text-center mt-8">
          <button
            onClick={handleShowMore}
            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-stone-900 hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-700"
          >
            {t('conferences.showMore', 'Show More')}
          </button>
        </div>
      )}
    </div>
  );
};

export default YouTubeStreams;